import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import type {
  LifestyleAddiction,
  LifestyleSubstanceFrequency,
  LifestyleSubstanceType,
} from "@louise/medical-history/basic-info/types";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { buildSubstancesNavigationStack } from "./buildSubstancesNavigationStack";

export type SubstanceType = LifestyleSubstanceType;
export type SubstanceFrequency = LifestyleSubstanceFrequency;

export interface SubstanceDetail extends LifestyleAddiction {
  substance: SubstanceType | null;
  frequency?: SubstanceFrequency | null;
  averageQuantity?: number | null;
  otherFrequency?: string | null;
  startDate?: string | null;
  stopDate?: string | null;
  notes?: string | null;
  other?: string | null;
}

export type SubstancesFlowRoute =
  | "SubstancesIntro"
  | "SubstancesSelect"
  | "SubstancesComplete"
  | { type: "SubstanceDetails"; substance: SubstanceType };

export interface SubstancesState {
  hasSubstances?: boolean | null;
  selectedSubstances: SubstanceType[];
  substanceDetails: SubstanceDetail[];
  substanceIdsToDelete: string[];
  navigationStack: SubstancesFlowRoute[];
  currentNavigationIndex: number;
}

const initialState: SubstancesState = {
  hasSubstances: null,
  selectedSubstances: [],
  substanceDetails: [],
  substanceIdsToDelete: [],
  navigationStack: ["SubstancesIntro"],
  currentNavigationIndex: 0,
};

interface SubstancesContextValue {
  state: SubstancesState;
  updateState: (updates: Partial<SubstancesState>) => void;
  resetState: () => void;
}

const SubstancesContext = createContext<SubstancesContextValue | null>(null);

export function SubstancesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: patient, isLoading } = usePatient();
  const [state, setState] = useState<SubstancesState>(initialState);

  // Load data from patient (like InfertilityContext)
  useEffect(() => {
    // Wait for patient data to load
    if (isLoading) {
      return;
    }

    if (!patient) {
      return;
    }

    const lifestyle = patient.lifestyle;
    if (!lifestyle) {
      return;
    }

    const addictions = lifestyle.addictions ?? [];

    setState((prev) => {
      // Check if state is in initial empty state - if so, always try to load
      const isInitialState =
        prev.hasSubstances === null && prev.substanceDetails.length === 0;

      const next: Partial<SubstancesState> = {};

      // Load hasSubstances (only if empty or initial state)
      if (
        (isInitialState || prev.hasSubstances === null) &&
        lifestyle.hasAddictions != null
      ) {
        next.hasSubstances = lifestyle.hasAddictions;
      }

      // Load substanceDetails ONLY if state is truly empty (initial state)
      // Once user has interacted, don't reload from DB to preserve their changes
      if (isInitialState && addictions.length > 0) {
        const selected = Array.from(
          new Set(
            addictions
              .map((item: any) => item?.substance)
              .filter((s: unknown): s is SubstanceType => Boolean(s)),
          ),
        );

        next.selectedSubstances = selected;
        next.substanceDetails = addictions.map((item: any) => ({
          id: item?.id ?? undefined,
          substance: (item?.substance ?? null) as SubstanceType | null,
          frequency: (item?.frequency ?? null) as SubstanceFrequency | null,
          averageQuantity:
            typeof item?.averageQuantity === "number"
              ? item.averageQuantity
              : null,
          otherFrequency:
            typeof item?.otherFrequency === "string" ? item.otherFrequency : null,
          startDate:
            typeof item?.startDate === "string" && item.startDate.length > 0
              ? item.startDate
              : null,
          stopDate:
            typeof item?.stopDate === "string" && item.stopDate.length > 0
              ? item.stopDate
              : null,
          notes: typeof item?.notes === "string" ? item.notes : null,
          other: typeof item?.other === "string" ? item.other : null,
        }));
      }

      // Only update if there are changes
      if (Object.keys(next).length === 0) {
        return prev;
      }

      const mergedState = { ...prev, ...next };
      const stack = buildSubstancesNavigationStack(mergedState);

      // Merge with previous state (don't replace)
      return {
        ...mergedState,
        navigationStack: stack,
        currentNavigationIndex: Math.min(
          prev.currentNavigationIndex ?? 0,
          Math.max(stack.length - 1, 0),
        ),
      };
    });
  }, [patient, isLoading]);

  const updateState = useCallback((updates: Partial<SubstancesState>) => {
    setState((prev) => {
      const newState = { ...prev, ...updates };
      const stack = buildSubstancesNavigationStack(newState);
      return {
        ...newState,
        navigationStack: stack,
        currentNavigationIndex: Math.min(
          newState.currentNavigationIndex ?? 0,
          Math.max(stack.length - 1, 0),
        ),
      };
    });
  }, []);

  const resetState = useCallback(() => setState(initialState), []);

  return (
    <SubstancesContext.Provider value={{ state, updateState, resetState }}>
      {children}
    </SubstancesContext.Provider>
  );
}

export function useSubstances() {
  const ctx = useContext(SubstancesContext);
  if (!ctx) {
    throw new Error("useSubstances must be used within SubstancesProvider");
  }
  return ctx;
}
